import { ReactNode } from "react";
import { useOpenSupportChat } from "shared/hooks/use-open-support-chat";
import { Button, ButtonVariant, Show } from "shared/components";

import failedIcon from "shared/icons/status-icons/failed.svg";
import { PrintReady } from "../providers/PrintProvider";
import { CodeAndDescription } from "types/landlord/reports";
import {
  errorHitCodes,
  HitCodes
} from "views/applications-id-report/slices/CreditReportSlice/utils";
import { RefreshCCW02 } from "untitledui-js-base";
import { RerunRequirement } from "shared/types/verification";
import { ApplicationStatus } from "shared/types";
import { ResumeTooltip } from "shared/app/OnHoldResume/ResumeTooltip";
import { getRerunRequirementMessage } from "../utils/getRerunRequirementMessage";

export const ReportException = ({
  applicationId,
  title,
  verificationId,
  hitCode,
  onRerunReport,
  rerunRequirements,
  applicationStatus
}: {
  applicationId: string | number;
  title?: string;
  verificationId?: number | string;
  hitCode?: CodeAndDescription | null;
  onRerunReport?: () => void;
  rerunRequirements?: RerunRequirement[];
  applicationStatus: ApplicationStatus;
}) => {
  const openSupportChat = useOpenSupportChat("report_exception");
  const rerunRequirementMessage = getRerunRequirementMessage({
    requirement: rerunRequirements?.[0],
    reportName: title
  });
  let rerunTooltip: ReactNode = "";
  if (rerunRequirementMessage) rerunTooltip = rerunRequirementMessage;
  else if (applicationStatus === ApplicationStatus.OnHold)
    rerunTooltip = <ResumeTooltip applicationId={applicationId} />;

  let reportDescription;
  if (hitCode?.code && HitCodes[hitCode.code])
    reportDescription = HitCodes[hitCode.code];
  else {
    const rerunText = Boolean(onRerunReport) ? "or re-run report" : "";
    reportDescription = `Applicant has successfully completed the application, but we're
            encountering an error retrieving the report from the data provider.
            Please contact our support team to resolve this issue ${rerunText}`;
  }

  return (
    <div className="flex h-full flex-col">
      {title && (
        <h1 className="text-lg font-bold text-content-1000">{title}</h1>
      )}
      {verificationId ? <PrintReady verificationId={verificationId} /> : null}

      <div className="mx-auto flex max-w-[531px] grow flex-col items-center justify-center gap-6">
        <div className="bg-danger-50 p-5 rounded-full">
          <img
            src={failedIcon}
            className="rotate-180"
            alt="danger exclamation mark"
            height={24}
            width={24}
          />
        </div>

        <div>
          <h2 className="text-center text-base font-bold text-content-500">
            {hitCode?.code &&
            errorHitCodes.includes(hitCode.code) &&
            HitCodes[hitCode.code]
              ? "Report unavailable"
              : "There's an error retrieving report"}
          </h2>
          <p className="mt-2 text-center text-sm font-medium text-content-300">
            {reportDescription}
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={() => {
              openSupportChat();
            }}
          >
            Contact support
          </Button>
          <Show if={Boolean(onRerunReport)}>
            <Button
              variant={ButtonVariant.BASE}
              Icon={RefreshCCW02}
              tooltipDelay={250}
              tooltip={rerunTooltip}
              disabled={
                Boolean(rerunRequirements?.length) ||
                applicationStatus === ApplicationStatus.OnHold
              }
              tooltipPlacement="bottom"
              onClick={onRerunReport}
            >
              Re-run report
            </Button>
          </Show>
        </div>
      </div>
    </div>
  );
};
